import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { showToast } from './Toast';
import {
  LOCATION_LABELS, LOCATION_IDS, JAMES_LABELS, NOAH_LABELS,
  formatDate, dayOfWeek, ROLE_CHECKLIST_TYPE,
  BRANDS, SERVICE_OPTIONS, DEAL_STATUS_OPTIONS, dealClass,
} from '../helpers';
import { useCarModels } from '../useCarModels';
import { BrandTag, LocationBadge } from './Badges';
import { SalespersonSelect } from './EditableCell';

const STATUS_OPTIONS = [
  { value: 'na',   label: 'N/A' },
  { value: 'ns',   label: 'Not Started' },
  { value: 'prog', label: 'In Progress' },
  { value: 'done', label: 'Complete' },
];

// Friendly labels for activity_log actions
const ACTION_LABELS = {
  vehicle_created:      '➕ Created',
  vehicle_updated:      '✏️ Updated',
  vehicle_archived:     '📦 Archived',
  vehicle_unarchived:   '↩️ Restored',
  checklist_completed:  '📋 Checklist done',
  status_detail_prog:   '✨ Detail started (Acts)',
  status_detail_done:   '✨ Detail finished (Acts)',
};

const STATUS_WORD = { na: 'N/A', ns: 'Not Started', prog: 'In Progress', done: 'Complete' };

// Render one activity entry's details as short "field: value" chips
function detailChips(details) {
  if (!details || typeof details !== 'object') return null;
  const skip = new Set(['via']);
  return Object.entries(details)
    .filter(([k, v]) => !skip.has(k) && v !== null && v !== undefined && typeof v !== 'object')
    .slice(0, 6)
    .map(([k, v]) => {
      const label = k.replace('status_', '').replace(/_/g, ' ');
      const val = STATUS_WORD[v] !== undefined && k.startsWith('status_') ? STATUS_WORD[v] : String(v).slice(0, 40);
      return `${label}: ${val}`;
    });
}

function HistorySection({ vehicleId }) {
  const [open, setOpen]       = useState(false);
  const [rows, setRows]       = useState(null);
  const [loading, setLoading] = useState(false);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && rows === null) {
      setLoading(true);
      try { setRows(await api.vehicleActivity(vehicleId)); }
      catch { setRows([]); }
      finally { setLoading(false); }
    }
  }

  return (
    <div style={{ marginTop: 14 }}>
      <button type="button" className="ab" style={{ width: '100%' }} onClick={toggle}>
        🕐 {open ? 'Hide History' : 'Show History'}
      </button>
      {open && (
        <div style={{
          marginTop: 8, background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '4px 12px', maxHeight: 260, overflowY: 'auto',
        }}>
          {loading && <div style={{ padding: 10, fontSize: 12, color: 'var(--muted)' }}>Loading…</div>}
          {rows && rows.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--muted)' }}>No history yet.</div>
          )}
          {(rows || []).map((r) => {
            const dt = new Date(r.created_at);
            const when = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
            const chips = detailChips(r.details);
            return (
              <div key={r.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {ACTION_LABELS[r.action] || r.action}
                    <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {r.operator || '—'}{r.role ? ` (${r.role})` : ''}</span>
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{when}</span>
                </div>
                {chips && chips.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {chips.map((c, i) => (
                      <span key={i} style={{
                        fontSize: 10, background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 20, padding: '1px 8px', color: 'var(--mid)',
                      }}>{c}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Convert delivery_date (YYYY-MM-DD) + delivery_time ('HH:MM' or '4:00 PM')
// → datetime-local string (YYYY-MM-DDTHH:MM). If time can't be parsed, returns
// just the date with empty time portion.
function toDatetimeLocal(date, time) {
  if (!date) return '';
  const d = String(date).slice(0, 10);
  if (!time) return `${d}T00:00`;
  // 24h "HH:MM" — keep as is
  let m = String(time).match(/^(\d{1,2}):(\d{2})$/);
  if (m) return `${d}T${m[1].padStart(2, '0')}:${m[2]}`;
  // 12h "H:MM AM/PM"
  m = String(time).match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m) {
    let h = parseInt(m[1]);
    const mm = m[2];
    const ampm = m[3].toUpperCase();
    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return `${d}T${String(h).padStart(2, '0')}:${mm}`;
  }
  return `${d}T00:00`;
}

// Stock helpers (mirrors AddVehicle)
function brandCfgFor(brandId) {
  return BRANDS.find((b) => b.id === brandId) || BRANDS[0];
}

// Split an existing stock_number ("K123456" / "1ABC234") into the digit/free
// parts the editor uses, given the new brand selection.
function splitStock(stockNumber, brandId) {
  const cfg = brandCfgFor(brandId);
  const sn = String(stockNumber || '');
  if (cfg.id === 'SERVICE') return { digits: '', free: sn };
  // Strip the prefix if present
  const stripped = cfg.prefix && sn.startsWith(cfg.prefix) ? sn.slice(cfg.prefix.length) : sn;
  // Alphanumeric brands (GAC) keep letters; others digits-only
  const cleaned = cfg.alphanumeric
    ? stripped.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, cfg.digits)
    : stripped.replace(/\D/g, '').slice(0, cfg.digits);
  return { digits: cleaned, free: '' };
}

export default function VehicleModal({
  open, vehicleId, session,
  onClose, onSaved, onArchived, onDeleted, onOpenChecklist,
}) {
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(null);
  const { grouped: modelsByBrand } = useCarModels();
  const OTHER_VALUE = '__other__';

  useEffect(() => {
    if (!open || !vehicleId) return;
    setLoading(true);
    api.getVehicle(vehicleId)
      .then((v) => {
        setVehicle(v);
        const parts = splitStock(v.stock_number, v.brand);
        setForm({
          // Vehicle details
          brand:           v.brand,
          model:           v.model || '',
          stockDigits:     parts.digits,
          stockFree:       parts.free,
          ro_number:       v.ro_number || '',
          colour:          v.colour    || '',
          deal_status:     v.deal_status || 'SOLD',
          delivery_dt:     toDatetimeLocal(v.delivery_date, v.delivery_time),
          salesperson:     v.salesperson || '',
          services:        Array.isArray(v.services) ? v.services : [],
          is_ev:           !!v.is_ev,
          // Workflow
          location:        v.location,
          status_workshop: v.status_workshop || 'na',
          status_tint:     v.status_tint     || 'na',
          status_detail:   v.status_detail   || 'na',
          status_paint:    v.status_paint    || 'na',
          james_status:    v.james_status    || 0,
          noah_status:     v.noah_status     || 0,
          notes:           v.notes           || '',
        });
      })
      .catch((e) => showToast('Failed to load: ' + e.message))
      .finally(() => setLoading(false));
  }, [open, vehicleId]);

  const brandCfg = useMemo(
    () => (form ? brandCfgFor(form.brand) : BRANDS[0]),
    [form]
  );
  const modelList = form ? (modelsByBrand[form.brand] || []) : [];
  const isFreeModel = brandCfg.freeModel;
  const isService   = form?.brand === 'SERVICE';
  // If editing a vehicle whose model is NOT in the dropdown list, show as "Other"
  const modelInList = form && modelList.includes(form.model);
  const showOtherForModel = form && !isFreeModel && form.model && !modelInList;

  if (!open) return null;

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function changeBrand(newBrand) {
    setForm((f) => {
      // Recompute stock parts for the new brand layout
      const composed = (f.brand === 'SERVICE') ? f.stockFree : (brandCfgFor(f.brand).prefix + f.stockDigits);
      const parts = splitStock(composed, newBrand);
      return {
        ...f,
        brand: newBrand,
        // Reset model only if the current one isn't valid for the new brand
        model: (modelsByBrand[newBrand] || []).includes(f.model) ? f.model : '',
        stockDigits: parts.digits,
        stockFree:   parts.free,
      };
    });
  }

  function changeModel(val) {
    if (val === OTHER_VALUE) {
      setForm((f) => ({ ...f, model: '' })); // user will type the new name
    } else {
      setForm((f) => ({ ...f, model: val }));
    }
  }

  function stockOnChange(e) {
    if (isService) {
      set('stockFree', e.target.value.toUpperCase().slice(0, 12));
    } else if (brandCfg.alphanumeric) {
      const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, brandCfg.digits);
      set('stockDigits', cleaned);
    } else {
      const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, brandCfg.digits);
      set('stockDigits', onlyDigits);
    }
  }

  function composedStock() {
    if (isService) return form.stockFree.trim();
    if (!form.stockDigits) return '';
    return brandCfg.prefix + form.stockDigits;
  }

  function toggleService(s) {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));
  }

  async function save() {
    if (saving || !form) return;

    const finalStock = composedStock();
    if (!finalStock) return showToast(`Stock #: ${brandCfg.stockHint}`);
    if (!form.model.trim()) return showToast('Pick or type a model');

    setSaving(true);
    try {
      // Split delivery datetime
      let date = null, time = null;
      if (form.delivery_dt) {
        const [d, t] = form.delivery_dt.split('T');
        date = d;
        time = t || null;
      }

      await api.updateVehicle(vehicleId, {
        brand:        form.brand,
        model:        form.model.trim(),
        stock_number: finalStock,
        ro_number:    form.ro_number.trim() || null,
        colour:       form.colour.trim()    || null,
        deal_status:  form.deal_status,
        delivery_date: date,
        delivery_time: time,
        salesperson:  form.salesperson.trim() || null,
        services:     form.services,
        is_ev:        form.is_ev,
        location:     form.location,
        status_workshop: form.status_workshop,
        status_tint:     form.status_tint,
        status_detail:   form.status_detail,
        status_paint:    form.status_paint,
        james_status:    form.james_status,
        noah_status:     form.noah_status,
        notes:           form.notes,
      });
      showToast('Vehicle updated successfully!');
      onSaved?.();
      onClose();
    } catch (e) {
      showToast('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function archive() {
    if (!window.confirm('Archive this vehicle?')) return;
    try {
      await api.archive(vehicleId);
      showToast('Vehicle archived');
      onArchived?.();
      onClose();
    } catch (e) {
      showToast('Failed: ' + e.message);
    }
  }

  async function remove() {
    if (!vehicle) return;
    const confirmMsg = `PERMANENTLY DELETE this vehicle?\n\n${vehicle.model} — ${vehicle.stock_number}\n\nThis cannot be undone. Checklists and history will also be removed.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.deleteVehicle(vehicleId);
      showToast('Vehicle deleted');
      onDeleted?.();
      onClose();
    } catch (e) {
      showToast('Failed: ' + e.message);
    }
  }

  // ── Restricted view for Mechanic / Detail / Tint roles ──
  // They can only edit their own status_* column and open their checklist;
  // everything else is read-only.
  const isRestricted = ['Mechanic', 'Detail', 'Tint'].includes(session.role);
  const myStatusKey  = session.role === 'Detail'   ? 'status_detail'
                     : session.role === 'Tint'     ? 'status_tint'
                     : session.role === 'Mechanic' ? 'status_workshop'
                     : null;
  const myStatusLabel = session.role === 'Detail'   ? '✨ Detail'
                      : session.role === 'Tint'     ? '🪟 Tint'
                      : session.role === 'Mechanic' ? '🔧 Workshop'
                      : '';

  if (isRestricted) {
    return (
      <div className="mo open" onClick={(e) => { if (e.target.classList.contains('mo')) onClose(); }}>
        <div className="modal" style={{ maxWidth: 480 }}>
          {loading || !vehicle || !form ? (
            <div className="loading">Loading…</div>
          ) : (
            <>
              <div className="mh">
                <div>
                  <div className="mtit">{vehicle.model} — {vehicle.stock_number}</div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <BrandTag brand={form.brand} />
                    <LocationBadge location={form.location} />
                    {vehicle.delivery_date && (
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {formatDate(vehicle.delivery_date)} · {dayOfWeek(vehicle.delivery_date)}
                        {vehicle.delivery_time ? ' · ' + vehicle.delivery_time : ''}
                      </span>
                    )}
                  </div>
                </div>
                <button className="mbclose" onClick={onClose}>✕</button>
              </div>

              <div className="mbody">
                {/* Quick details (read-only) */}
                <div className="ig">
                  <div><div className="il">Stock #</div><div className="iv">{vehicle.stock_number}</div></div>
                  <div><div className="il">Colour</div><div className="iv">{vehicle.colour || '—'}</div></div>
                  <div><div className="il">Salesperson</div><div className="iv">{vehicle.salesperson || '—'}</div></div>
                </div>

                {/* Services (read-only) */}
                <div className="sec" style={{ marginBottom: 7 }}>Services Required</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                  {(vehicle.services || []).length === 0 && <span style={{ fontSize: 12, color: 'var(--muted)' }}>None</span>}
                  {(vehicle.services || []).map((s) => (
                    <span key={s} className="btg used" style={{ fontSize: 11 }}>{s}</span>
                  ))}
                </div>

                {/* Notes (read-only) */}
                {vehicle.notes && (
                  <>
                    <div className="sec" style={{ marginBottom: 7 }}>Notes from Sales</div>
                    <div style={{
                      background: 'var(--bg)', border: '1px solid var(--border)',
                      borderRadius: 10, padding: 10, fontSize: 13, marginBottom: 14, whiteSpace: 'pre-wrap',
                    }}>{vehicle.notes}</div>
                  </>
                )}

                {/* My status — editable */}
                <div className="sec" style={{ marginBottom: 7 }}>{myStatusLabel} — Your Status</div>
                <div className="wfi" style={{ marginBottom: 14 }}>
                  <div className="wfi-l">Update your work status</div>
                  <select
                    className="wfsel"
                    value={form[myStatusKey]}
                    onChange={(e) => set(myStatusKey, e.target.value)}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                {/* Checklist button */}
                <button
                  className="ab g"
                  style={{ width: '100%', padding: 12, fontSize: 13 }}
                  onClick={() => onOpenChecklist?.(ROLE_CHECKLIST_TYPE[session.role], vehicle)}
                >📋 Open {session.role} Checklist</button>
              </div>

              <div className="mfoot">
                <div style={{ flex: 1, fontSize: 11, color: 'var(--muted)' }}>
                  You can only edit your own work status. Contact a Manager for other changes.
                </div>
                <button className="btn-cn" onClick={onClose}>Cancel</button>
                <button className="btn-sv" disabled={saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mo open" onClick={(e) => { if (e.target.classList.contains('mo')) onClose(); }}>
      <div className="modal">
        {loading || !vehicle || !form ? (
          <div className="loading">Loading…</div>
        ) : (
          <>
            <div className="mh">
              <div>
                <div className="mtit">{vehicle.model} — {vehicle.stock_number}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={`deal ${dealClass(form.deal_status)}`}>{form.deal_status}</span>
                  <BrandTag brand={form.brand} />
                  <LocationBadge location={form.location} />
                  {vehicle.delivery_date && (
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                      Delivery: {formatDate(vehicle.delivery_date)} · {dayOfWeek(vehicle.delivery_date)}
                      {vehicle.delivery_time ? ' · ' + vehicle.delivery_time : ''}
                    </span>
                  )}
                </div>
              </div>
              <button className="mbclose" onClick={onClose}>✕</button>
            </div>

            <div className="mbody">
              {/* ─── Vehicle Details (editable) ─── */}
              <div className="sec" style={{ marginBottom: 10 }}>✏️ Vehicle Details</div>

              <div className="wfg">
                <div className="wfi">
                  <div className="wfi-l">Status</div>
                  <select className="wfsel" value={form.deal_status} onChange={(e) => set('deal_status', e.target.value)}>
                    {DEAL_STATUS_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="wfi">
                  <div className="wfi-l">Brand</div>
                  <select className="wfsel" value={form.brand} onChange={(e) => changeBrand(e.target.value)}>
                    {BRANDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                  </select>
                </div>
                <div className="wfi">
                  <div className="wfi-l">Model</div>
                  {isFreeModel ? (
                    <input
                      className="wfsel"
                      placeholder={isService ? 'e.g. Hilux' : 'Type model'}
                      value={form.model}
                      onChange={(e) => set('model', e.target.value)}
                    />
                  ) : (
                    <>
                      <select
                        className="wfsel"
                        value={showOtherForModel || form.model === '' ? OTHER_VALUE : form.model}
                        onChange={(e) => changeModel(e.target.value)}
                      >
                        <option value="">— Select model —</option>
                        {modelList.map((m) => <option key={m}>{m}</option>)}
                        <option value={OTHER_VALUE}>+ Other…</option>
                      </select>
                      {(showOtherForModel || (form.model === '' && !modelInList)) && (
                        <input
                          className="wfsel"
                          style={{ marginTop: 6 }}
                          placeholder="Type model"
                          value={form.model}
                          onChange={(e) => set('model', e.target.value)}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="wfg">
                <div className="wfi">
                  <div className="wfi-l">
                    Stock Number {!isService && brandCfg.prefix && (
                      <span style={{ color: 'var(--accent)' }}> · {brandCfg.prefix} auto</span>
                    )}
                  </div>
                  {isService ? (
                    <input
                      className="wfsel"
                      placeholder="Number plate"
                      value={form.stockFree}
                      onChange={stockOnChange}
                      style={{ textTransform: 'uppercase', letterSpacing: 1 }}
                    />
                  ) : (
                    <div style={{ position: 'relative' }}>
                      {brandCfg.prefix && (
                        <span style={{
                          position: 'absolute', left: 11, top: 0, bottom: 0,
                          display: 'flex', alignItems: 'center',
                          fontSize: 13, fontWeight: 700, color: 'var(--muted)',
                          pointerEvents: 'none', zIndex: 1,
                        }}>{brandCfg.prefix}</span>
                      )}
                      <input
                        className="wfsel"
                        placeholder={brandCfg.alphanumeric ? `${brandCfg.digits} chars` : `${brandCfg.digits} digits`}
                        value={form.stockDigits}
                        onChange={stockOnChange}
                        inputMode={brandCfg.alphanumeric ? 'text' : 'numeric'}
                        style={{ paddingLeft: brandCfg.prefix ? 24 : 10, textTransform: brandCfg.alphanumeric ? 'uppercase' : 'none' }}
                      />
                    </div>
                  )}
                </div>
                <div className="wfi">
                  <div className="wfi-l">RO Number</div>
                  <input
                    className="wfsel"
                    placeholder="733779"
                    value={form.ro_number}
                    onChange={(e) => set('ro_number', e.target.value)}
                  />
                </div>
              </div>

              <div className="wfg">
                <div className="wfi">
                  <div className="wfi-l">Colour</div>
                  <input
                    className="wfsel"
                    placeholder="White"
                    value={form.colour}
                    onChange={(e) => set('colour', e.target.value)}
                  />
                </div>
                <div className="wfi">
                  <div className="wfi-l">Delivery Date & Time</div>
                  <input
                    className="wfsel"
                    type="datetime-local"
                    value={form.delivery_dt}
                    onChange={(e) => set('delivery_dt', e.target.value)}
                  />
                </div>
              </div>

              <div className="wfg" style={{ gridTemplateColumns: '1fr 140px' }}>
                <div className="wfi">
                  <div className="wfi-l">Salesperson</div>
                  <SalespersonSelect
                    className="wfsel"
                    value={form.salesperson}
                    onChange={(val) => set('salesperson', val || '')}
                  />
                </div>
                <div className="wfi">
                  <div className="wfi-l">EV Vehicle</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '7px 0' }}>
                    <input
                      type="checkbox"
                      checked={form.is_ev}
                      onChange={(e) => set('is_ev', e.target.checked)}
                    />
                    <span style={{ fontSize: 12 }}>Electric</span>
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div className="wfi-l" style={{ marginBottom: 6 }}>Services Required</div>
                <div className="fg3">
                  {SERVICE_OPTIONS.map((s) => (
                    <div
                      key={s.id}
                      className={`et ${form.services.includes(s.id) ? 'sel' : ''}`}
                      onClick={() => toggleService(s.id)}
                    >{s.label}</div>
                  ))}
                </div>
              </div>

              {/* ─── Location ─── */}
              <div className="sec" style={{ marginBottom: 7 }}>📍 Location</div>
              <div className="lo3" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
                {LOCATION_IDS.map((loc) => (
                  <div
                    key={loc}
                    className={`lo ${loc} ${form.location === loc ? 'sel' : ''}`}
                    style={{ minWidth: 90 }}
                    onClick={() => set('location', loc)}
                  >📍 {LOCATION_LABELS[loc]}</div>
                ))}
              </div>

              {/* ─── Workflow status ─── */}
              <div className="sec" style={{ marginBottom: 7 }}>Workflow Status</div>
              <div className="wfg">
                {[
                  { k: 'status_workshop', label: '🔧 Workshop' },
                  { k: 'status_tint',     label: '🪟 Tint' },
                  { k: 'status_detail',   label: '✨ Detail' },
                  { k: 'status_paint',    label: '🛡️ Paint Pack' },
                ].map(({ k, label }) => (
                  <div className="wfi" key={k}>
                    <div className="wfi-l">{label}</div>
                    <select
                      className="wfsel"
                      value={form[k]}
                      onChange={(e) => set(k, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {/* ─── James ─── */}
              <div className="sec" style={{ marginBottom: 7 }}>🔑 Workshop — Key Handover</div>
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                  Update key handover status at the Workshop
                </div>
                <div className="jnrow">
                  {JAMES_LABELS.map((label, i) => (
                    <div
                      key={i}
                      className={`jnopt ${form.james_status === i ? `sj${i}` : ''}`}
                      onClick={() => set('james_status', i)}
                    >{label}</div>
                  ))}
                </div>
              </div>

              {/* ─── Noah ─── */}
              <div className="sec" style={{ marginBottom: 7 }}>🚗 Dealership — Delivery Status</div>
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                  Update delivery status at the Dealership
                </div>
                <div className="jnrow">
                  {NOAH_LABELS.map((label, i) => (
                    <div
                      key={i}
                      className={`jnopt ${form.noah_status === i ? `sn${i}` : ''}`}
                      onClick={() => set('noah_status', i)}
                    >{label}</div>
                  ))}
                </div>
                <div style={{ marginTop: 10 }}>
                  <button
                    className="btn-in"
                    style={{ fontSize: 13, padding: 10 }}
                    onClick={() => onOpenChecklist?.('noah', vehicle)}
                  >📋 Open Final Delivery Checklist</button>
                </div>
              </div>

              {ROLE_CHECKLIST_TYPE[session.role] && session.role !== 'DeliveryMgr' && (
                <div style={{ marginBottom: 14 }}>
                  <button
                    className="ab g"
                    onClick={() => onOpenChecklist?.(ROLE_CHECKLIST_TYPE[session.role], vehicle)}
                  >📋 Open {session.role} Checklist</button>
                </div>
              )}

              {/* ─── Notes ─── */}
              <div className="sec" style={{ marginBottom: 7 }}>Notes</div>
              <textarea
                className="ta"
                placeholder="Notes for other teams..."
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
              />

              {/* ─── History (activity log) ─── */}
              <HistorySection vehicleId={vehicleId} />
            </div>

            <div className="mfoot">
              <button className="ab" onClick={archive} title="Move to Archive (recoverable)">📦 Archive</button>
              {session.role === 'Manager' && (
                <button
                  className="ab r"
                  onClick={remove}
                  style={{ marginLeft: 6 }}
                  title="Permanently delete — cannot be undone"
                >🗑️ Delete</button>
              )}
              <div style={{ flex: 1 }} />
              <button className="btn-cn" onClick={onClose}>Cancel</button>
              <button className="btn-sv" disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
